import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

import request from 'superagent';

import PAGES_TYPE from 'shared-constants/src/pagesType';

import SearchBuilder from 'shared-components/components/SearchBuilder';

import { computeActiveFilters } from 'shared-utils/src/helpersMyCasa';
import integerWithEuropeThousandSeparator from 'shared-utils/src/integerWithThousandSeparator';

import { setItem } from '@helpers/Storage';
import throwApireqDebugError from '@helpers/throwApireqDebugError';
import { scrollToTop } from '@helpers/DOMHelpers';
import { createDefaultAgencyName } from '@helpers/searchAgency/SearchAgency';
import {
  normalizeSRPUrl,
  computeHowToGoBack,
} from '@helpers/search/Search';

import getMapAddress from '@helpers/search/getMapAddress';

import saveRLSRecord from '@helpers/saveRLSRecord';

import UiHandlerHOC from '../app/UiHandler';

import { fetchSearch } from '../../common/redux/search/actions';

import { fetchLocationById } from '../components/common/search/helpers/search';

import { filtersAndLocationsToQObject } from '../../common/lib/filtersToQObject';

import getEssentialLocationData from '../helpers/search/getEssentialLocationData';

import Search from './Search';

const createInitialFilters = (serverFilters, agencyFilters) => {
  const agencyFreetext = createDefaultAgencyName(agencyFilters);
  return { ...serverFilters, agencyData: Object.keys(agencyFreetext).length > 0 ? { ...agencyFreetext } : null };
};

const SearchPageComponent = (props) => {
  const {
    device,
    orientation,
    config: {
      esapi,
      siteName,
    },
    intl: {
      currentLocale,
    },
    search,
    search: {
      locale,
      domain,
      list,
      fewResults,
      clientFilters: {
        filters: serverFilters,
        activeFiltersOnSSr,
      },
      queryFilters,
      queryParameters: serverParameters,
      agencyFilters,
      locations: serverLocations,
      parentLocation: serverParentLocations,
      hasPublisherHeading,
      isPublisherHP,
      isAgencySrp,
      paginator,
      availableResults,
      uri: serverUri,
      apireq: serverApireq,
      isThinTown,
      mapAddressData,
    },
    actions: {
      fetchListingOnFiltersChange,
      fetchSRPAvailableResults,
      fetchAgencyListingOnFiltersChange,
    },
    location: {
      pathname,
    },
    history: {
      goBack,
    },
  } = props;

  const [initialFilters] = useState(createInitialFilters(serverFilters, agencyFilters));
  const [initialParameters] = useState(serverParameters || { sortType: 'relevance' });
  const [initialLocations] = useState({
    locations: serverLocations,
    parent: serverParentLocations && serverParentLocations.length ? serverParentLocations[0] : {},
  });

  const [initialMapAddressData] = useState(mapAddressData);

  const activeFiltersOnSSrRef = useRef(activeFiltersOnSSr || {
    activeFiltersList: {},
    activeFiltersCount: 0,
  });

  const [forcedAuction, setForcedAuctionState] = useState(false);
  const [collapsePublisherInfos, setCollapsePublisherInfos] = useState(false);
  const [fillInExpanded, setFillInExpanded] = useState(fewResults ? fewResults.startsExpanded : false);
  const [userSortedNewAds, setUserSortedNewAds] = useState(false);

  const [mapAddressResolved, setMapAddressResolved] = useState(!mapAddressData);
  const rlsSavedRef = useRef(false);

  const [backUriCollection, setBackUriCollection] = useState([{
    uri: serverUri,
    filters: initialFilters,
    locations: initialLocations.locations,
    parentLocation: initialLocations.parent,
    isPublisherHP,
    isAgencySrp,
    agencyFreetext: initialFilters.agencyData,
  }]);
  // It needs to be a state because it's used in the back button
  // and it has to know if the floorplan is opened or not
  const [floorplanOpened, setFloorplanOpened] = useState(false);

  const computeAPIReq = async ({
    locations,
    filters,
    parameters,
    forceMap = false,
  }) => {
    let qObject;
    try {
      qObject = filtersAndLocationsToQObject(filters, locations);
      const proxy = await request.post('/portal-srp/api/v1/apireq')
        .set('Content-Type', 'application/json')
        .set('Accept', 'application/json')
        .withCredentials()
        .send({
          qObject,
          parameters,
          locations,
          apireqParams: {
            pathname,
            domain: siteName,
            locale: currentLocale,
            esapi,
            publisherPage: false,
            isThinTown,
          },
          pageType: forceMap ? PAGES_TYPE.SRP_MAP : PAGES_TYPE.SRP,
        });
      const { body } = proxy;
      return body;
    } catch (error) {
      throwApireqDebugError('SearchPage', error, locations, qObject);
    }
  };

  const computeAgencyAPIReq = async ({
    locations,
    activeFilters,
    agencyItem,
  }) => {    
    const proxy = await request.post('/portal-srp/api/v1/apireq/agency')
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json')
      .withCredentials()
      .send({
        config: {
          domain,
          locale,
          esapiConfig: esapi,
        },
        params: {
          locations,
          activeFilters,
          agencyItem,
          type: 'agencySrp',
        },
      });
    const { body: { apireq } } = proxy;
    return apireq;
  };

  const fetchSBAvailableResults = async ({
    locations,
    filters,
  }) => {
    let apireq;
    let proxyUrl;
    if (isAgencySrp) {
      const agencyItem = filters.agencyData || {};
      const activeFilters = { channel: filters.tr };
      delete activeFilters.agencyData;
      apireq = await computeAgencyAPIReq({
        locations,
        activeFilters,
        agencyItem,
      });
      proxyUrl = '/portal-srp/api/v1/agencySearch/availableresults';
    } else {
      apireq = await computeAPIReq({
        locations,
        filters,
      });
      proxyUrl = '/portal-srp/api/v1/search/availableresults';
    }
    if (!apireq) return { total: 0, ptgrpsAggregates: null, nearbyGeoJSON: null };

    const proxy = await request.post(proxyUrl)
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json')
      .withCredentials()
      .send({ apireq: apireq.apireq, queryFilters: apireq.queryFilters });

    return {
      total: proxy?.body?.total || 0,
      ptgrpsAggregates: proxy?.body?.ptgrpsAggregates || null,
      nearbyGeoJSON: proxy?.body?.nearbyGeoJSON || null,
    };
  };

  const updateBackUriCollection = ({
    newuri,
    locations,
    parentLocation,
    filters,
  }) => {
    if (newuri !== backUriCollection[backUriCollection.length - 1].uri) {
      const newCollection = [...backUriCollection, {
        uri: newuri,
        filters,
        locations,
        parentLocation,
        isPublisherHP,
        isAgencySrp,
        agencyFreetext: filters.agencyData,
      }];
      setBackUriCollection(newCollection);
    }
  };

  const fetchResults = async ({
    locations,
    parentLocation,
    filters,
    parameters,
    keepPage,
    mobileUpdate,
    fromForcedAuction,
  }) => {
    if (isAgencySrp) {
      const agencyItem = filters.agencyData || {};
      const sortType = (parameters && parameters.sortType && parameters.sortType.indexOf('relevance') > -1) ? null : (parameters && parameters.sortType);
      const activeFilters = { channel: filters.tr, ...sortType && { sortType } };
      delete activeFilters.agencyData;
      const apireq = await computeAgencyAPIReq({
        locations,
        activeFilters,
        agencyItem,
      });
      // if (device === 'smartphone' && mobileUpdate) {
      //   console.log('entro qua aa >>:',);
      //   apireq.apireq.size = 0;
      //   fetchSRPAgencyAvailableResults(apireq);
      // } else {

      updateBackUriCollection({
        newuri: apireq.uri,
        locations,
        parentLocation,
        filters,
      });
      fetchAgencyListingOnFiltersChange(apireq);

    } else {
      const apireq = await computeAPIReq({
        locations,
        filters,
        parameters,
      });
      if (!apireq) return;

      if (isPublisherHP) {
        // Full navigation to reset isPublisherHP flag server-side
        window.location.href = apireq.uri;
      } else if (hasPublisherHeading) {
        setCollapsePublisherInfos(true);
        setTimeout(
          () => {
            updateBackUriCollection({
              newuri: apireq.uri,
              locations,
              parentLocation,
              filters,
            });
            fetchListingOnFiltersChange(apireq);
            scrollToTop();
          }, 651,
        );
      } else if (
        device === 'desktop'
        || (
          device === 'tablet'
          && orientation === 'landscape'
        )
        || mobileUpdate
      ) {
        if (!filters.pId) {
          saveRLSRecord({
            qObject: apireq.queryFilters,
            locations,
            parentLocation,
            filters,
          });
        }

        if (keepPage) {
          const page = paginator.currentPage || 1;
          if (page !== 1) {
            const uri = apireq.uri.indexOf('?') > -1
              ? `${apireq.uri}&page=${page}`
              : `${apireq.uri.slice(0, -1)}?page=${page}`;
            apireq.uri = uri;
            apireq.apireq.page = page;
            apireq.queryParameters = apireq.queryParameters || {};
            apireq.queryParameters.page = page;
          }
        }

        if (fromForcedAuction || forcedAuction) {
          const newForcedAuction = fromForcedAuction || false;
          setForcedAuctionState(newForcedAuction);
        }

        updateBackUriCollection({
          newuri: apireq.uri,
          locations,
          parentLocation,
          filters,
        });


        if (fillInExpanded) {
          apireq.fillInExpanded = true;
        }

        fetchListingOnFiltersChange(apireq);
      } else {
        fetchSRPAvailableResults(apireq);
      }
    }
  };

  const startSearch = async ({
    locations,
    parentLocation,
    filters,
    forceMap,
  }) => {
    let apireq;
    if (isAgencySrp) {
      const agencyItem = filters.agencyData || {};
      const activeFilters = { ...filters };
      delete activeFilters.agencyData;
      apireq = await computeAgencyAPIReq({
        locations,
        activeFilters,
        agencyItem,
      });
    } else {
      apireq = await computeAPIReq({
        locations,
        filters,
        forceMap,
      });
      if (!apireq) return;

      saveRLSRecord({
        qObject: apireq.queryFilters,
        locations,
        parentLocation,
        filters,
      });

      const rslObj = {
        locations,
        parentLocation,
        filters,
      };      

      saveRLSRecord(rslObj);
    }

    const newUrl = normalizeSRPUrl(apireq.uri);
    document.location = newUrl;
  };

  const computeLastListingData = (isFillin = false) => {
    const tot = parseInt(availableResults.replace('.', ''), 10);
    if (fillInExpanded || isFillin) {
      const fr = fewResults || { listings: [], total: '0' };
      return {
        llItems: [...list, ...fr.listings],
        llTotal: tot + parseInt(fr.total, 10),
        llfillIn: 1,
      };
    }
    if (fewResults) {
      if (fewResults.page === 1) {
        if (fewResults.isNoResults && fewResults.listings.length > 0) {
          const [first, second, third] = fewResults.listings;
          const available = [first, second, third].filter(Boolean);
          return {
            llItems: available,
            llTotal: available.length,
            llfillIn: 0,
          };
        }
      } else {
        return {
          llItems: [...list],
          llTotal: tot + parseInt(fewResults.total, 10),
          llfillIn: 1,
        };
      }
    }
    return {
      llItems: [...list],
      llTotal: tot,
      llfillIn: 0,
    };
  };

  const setLastListingRecord = async ({
    href,
    classList,
    locations,
    filters,
    isFillin,    
  }) => {
    const loc = document.location;
    const page = paginator.currentPage || 1;

    const { llItems, llTotal, llfillIn } = computeLastListingData(isFillin);

    const lastListingParams = loc.search.substring(1).split('&');
    if (llfillIn) {
      lastListingParams.push('fillin=open');
    }

    const params = lastListingParams.reduce((acc, item) => {
      const [key, ...values] = item.split('=');
      key && (acc[key] = values.join('='));
      return acc;
    }, {});

    const apireq = await computeAPIReq({
      locations,
      filters,
    });
    if (!apireq) return;

    const artId = href.replace('immobili', '').replace(/\//g, '');
    const openedFrom = classList.contains('csaSrpcard__units__grid')
      ? artId.split('#')[0]
      : artId;

    let pages = paginator.totalPages;
    if (!pages && llItems.length) {
      pages = 1;
    }

    setItem('backToListing', 1); // Able to use history back from PDP
    setItem('__lastListing', JSON.stringify({
      isFromMap: false,
      page,
      pages,
      perPage: paginator.articlesForPage,
      items: { [page]: llItems.map((el) => el.id) },
      total: integerWithEuropeThousandSeparator(llTotal),
      path: loc.pathname,
      params,
      apireq,
      openedFrom,
      isPublisherHP,
      isFillin,
      locations,
      timestamp: new Date().getTime(),
    }));
  };

  const doLoadDetail = async (evt, href, isFillin, rightCLick, locations, filters) => {
    if (!rightCLick && !evt.metaKey) {
      evt.preventDefault();
    }
    const tgt = evt.target;
    const { classList } = tgt;

    // const skipClasses = [
    //   'modal_handle',
    //   'sp',
    //   'publisherDetail',
    //   'expand',
    // ];

    // const skipTxts = [
    //   'Agenzia',
    //   'Costruttore',
    // ];

    // if (
    //   skipTxts.indexOf(textContent) >= 0
    //   || (classList && skipClasses.some(n => classList.contains(n)))
    // ) {
    //   return;
    // }

    await setLastListingRecord({
      href,
      classList,
      locations,
      filters,
      isFillin,
    });

    if (!rightCLick && !evt.metaKey) {
      const destinationUrl = document.location.host.includes('local')
        ? `http://local.casa.it:3000${href}`
        : href;

      document.location = destinationUrl;      
    }
  };

  return (
    <>
      <SearchBuilder
        initialFilters={initialFilters}
        initialLocations={initialLocations}
        initialParameters={initialParameters}
        fetchLocationById={fetchLocationById}
      >
        {
          (sbProps) => {
            const {
              setCurrentNearby,
              setCurrentFilters,
              setCurrentLocation,
              setCurrentParameters,
              computeSearchFilters,
              setMapAddressInfos,
            } = sbProps;

            if (typeof window !== 'undefined' && mapAddressResolved && !rlsSavedRef.current && !isAgencySrp && !serverFilters.pId) {
              rlsSavedRef.current = true;
              saveRLSRecord({
                qObject: queryFilters,
                locations: sbProps.locations.search,
                parentLocation: sbProps.locations.searchParent,
              });
            }

            const { sortType: _sortType, ...sbfilters } = sbProps.filters;
            const sbparameters = sbProps.parameters || initialParameters;

            const setFilters = (newfilter, forceMobileUpdate = false) => setCurrentFilters({ ...sbfilters, ...newfilter }, (updatedFilters) => {
              console.log({ forceMobileUpdate });
              const mobileUpdate = forceMobileUpdate || Object.keys(newfilter)[0] === 'agencyData';
              const definitiveFilters = { ...updatedFilters };

              setFillInExpanded(false);

              const fetchParams = {
                locations: sbProps.locations.search,
                parentLocation: sbProps.locations.searchParent,
                filters: definitiveFilters,
                parameters: sbparameters,
                mobileUpdate,
              };

              fetchResults(fetchParams);
            });

            const setParameters = (newParams) => setCurrentParameters(newParams, (updatedParameters) => {
              /**
               * Every time we change the sort type we want to show
               * the banner for save search
               */
              setUserSortedNewAds(true);

              if (fewResults) {
                if (fewResults.page > 1) {
                  setCurrentFilters({ ...sbfilters, fillin: 'open' });
                }
              }

              const fetchParams = {
                locations: sbProps.locations.search,
                parentLocation: sbProps.locations.searchParent,
                filters: sbfilters,
                parameters: updatedParameters,
                mobileUpdate: true,
              };

              fetchResults(fetchParams);
            });

            const setNewSearchFromSaveSearchModal = ({
              newLocations,
              newParentLocation,
              newfilters,
            }) => {
              setCurrentLocation(newLocations, newParentLocation, {}, (updatedLocations, updatedParentLocation, tempFilters) => {
                setCurrentFilters({ ...tempFilters, ...newfilters }, (updatedFilters) => {
                  const fetchParams = {
                    locations: updatedLocations,
                    parentLocation: updatedParentLocation,
                    filters: updatedFilters,
                    mobileUpdate: true,
                  };
                  fetchResults(fetchParams);
                });
              });
            };

            const getAvailableResults = async ({
              newLocations = null,
              newFilters = {},
              deleteGeoFilters = false,
            }) => {
              const fetchParams = {
                locations: newLocations || sbProps.locations.search,
                filters: computeSearchFilters({ ...sbfilters, ...newFilters }),
              };

              // quando chiediamo gli available results con un cambio di località
              // ci viene chiesto di resettare eventuali filtri geo persistenti
              if (deleteGeoFilters) {
                delete fetchParams.filters.geobounds;
                delete fetchParams.filters.geocircle;
                delete fetchParams.filters.geopolygon;
              }

              if (!newFilters.qExpansion) {
                delete fetchParams.filters.qExpansion;
              }

              const av = await fetchSBAvailableResults(fetchParams);
              return av;
            };

            const setForcedAuction = () => setCurrentFilters({ ...sbfilters, isAuction: false }, (updatedFilters) => {
              const fetchParams = {
                locations: sbProps.locations.search,
                parentLocation: sbProps.locations.searchParent,
                filters: updatedFilters,
                mobileUpdate: true,
                fromForcedAuction: true,
              };
              fetchResults(fetchParams);
            });

            const setLocations = (choice, parent) => {
              setCurrentLocation(choice, parent, {}, (updatedLocations, updatedParentLocation, updatedFilters) => {
                const fetchParams = {
                  locations: updatedLocations,
                  parentLocation: updatedParentLocation,
                  filters: updatedFilters || sbProps.filters,
                };
                fetchResults(fetchParams);
              });
            };

            const callGetMapAddress = async () => {
              await getMapAddress({ data: initialMapAddressData, saveFn: (data) => { setMapAddressInfos(data); } });
              setMapAddressResolved(true);
            };

            const setNearby = (userNearby) => setCurrentNearby(userNearby, (updatedLocations, updatedParentLocation, updatedFilters) => {
              const fetchParams = {
                locations: updatedLocations,
                parentLocation: updatedParentLocation,
                filters: updatedFilters,
              };
              fetchResults(fetchParams);
            });

            const submitSearch = ({
              locations,
              parentLocation,
              filters,
              forceMap,
            }) => startSearch({
              locations: locations || sbProps.locations.search,
              parentLocation: parentLocation || sbProps.locations.searchParent,
              filters: filters || sbfilters,
              forceMap,
            });

            const loadDetail = (ev, href, isFillin = false, rightCLick = false) => doLoadDetail(ev, href, isFillin, rightCLick, sbProps.locations.search, sbProps.filters);

            const browserBackButton = async () => {
              if (backUriCollection.length > 1) {
                const params = {
                  domain,
                  currentLocale,
                  esapi,
                  fetchAgencyListingOnFiltersChange,
                  fetchListingOnFiltersChange,
                  goBack,
                  pageType: PAGES_TYPE.SRP,
                  backUriCollection,
                };
                const data = await computeHowToGoBack(params);
                setBackUriCollection(data.backUriCollection);
                setCurrentLocation(data.newState.locations, data.newState.parentLocation, {}, () => setCurrentFilters(data.newState.filters));
                setCollapsePublisherInfos(data.newState.collapsePublisherInfos);
              } else if (floorplanOpened) {
                setFloorplanOpened(false);
              } else {
                goBack();
              }
            };

            const startWithSelectedPtypes = sbfilters.propertyTypes && Array.isArray(sbfilters.propertyTypes)
              ? sbfilters.propertyTypes.length > 0
              : false;

            return (
              <Search
                filters={sbfilters}
                activeFilters={computeActiveFilters(sbProps.filters)}
                locations={sbProps.locations.search}
                parentLocation={sbProps.locations.searchParent}
                mainLocation={sbProps.locations.ui.main}
                additionalLocations={sbProps.locations.ui.additional}
                collapsePublisherInfos={collapsePublisherInfos}
                forcedAuction={forcedAuction}
                parameters={sbparameters}
                setFilters={setFilters}
                setParameters={setParameters}
                getAvailableResults={getAvailableResults}
                setForcedAuction={setForcedAuction}
                setLocations={setLocations}
                setNearby={setNearby}
                setNewSearchFromSaveSearchModal={setNewSearchFromSaveSearchModal}
                fillInExpanded={fillInExpanded}
                setFillInExpanded={setFillInExpanded}
                submitSearch={submitSearch}
                loadDetail={loadDetail}
                browserBackButton={browserBackButton}
                startWithSelectedPtypes={startWithSelectedPtypes}
                userSortedNewAds={userSortedNewAds}
                setFloorplanOpened={setFloorplanOpened}
                activeFiltersOnSSr={activeFiltersOnSSrRef.current}
                initialMapAddressData={initialMapAddressData}
                callGetMapAddress={callGetMapAddress}
                {...props}
              />
            );
          }
        }
      </SearchBuilder>
    </>
  );
};

SearchPageComponent.propTypes = {
  device: PropTypes.string,
  orientation: PropTypes.string,
  actions: PropTypes.instanceOf(Object),
  config: PropTypes.instanceOf(Object),
  search: PropTypes.instanceOf(Object),
  intl: PropTypes.instanceOf(Object),
  location: PropTypes.instanceOf(Object),
  history: PropTypes.instanceOf(Object),
  user: PropTypes.instanceOf(Object),
  isLocalStorageAvailable: PropTypes.bool,
};

SearchPageComponent.defaultProps = {
  device: '',
  orientation: '',
  actions: {},
  config: {},
  search: {},
  intl: {},
  location: {},
  history: {},
  user: {},
  isLocalStorageAvailable: false,
};

const SearchPage = UiHandlerHOC(SearchPageComponent, fetchSearch);
export default SearchPage;
